import React from 'react'
import ReactDOM from 'react-dom/client'
import './styles/index.css'

const violations = trafficCalculatorData.violations;
const violationTypes = trafficCalculatorData.violation_types;
const eligible = trafficCalculatorData.eligible;
const price = trafficCalculatorData.price;
const textSection = trafficCalculatorData.text_section;
const postContent = trafficCalculatorData.post_content;

function Tooltip({ content, children }) {
    const [isVisible, setIsVisible] = React.useState(false);
    
    return (
        <div className="relative inline-block">
            <div
                onMouseEnter={() => setIsVisible(true)}
                onMouseLeave={() => setIsVisible(false)}
            >
                {children}
            </div>
            {isVisible && (
                <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-6 py-3 text-sm bg-gray-900 text-white rounded shadow-lg min-w-[200px] text-center">
                    {content}
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
                </div>
            )}
        </div>
    );
}

function Card({ children, className = "" }) {
    return (
        <div className={`bg-white rounded-lg shadow-lg border border-gray-200 ${className}`}>
            {children}
        </div>
    );
}

function TrafficCalculator() {
    const [searchQuery, setSearchQuery] = React.useState('');
    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedViolation, setSelectedViolation] = React.useState(null);
    const [isBreakdownOpen, setIsBreakdownOpen] = React.useState(false);
    const [selectedIndex, setSelectedIndex] = React.useState(-1);
    const dropdownRef = React.useRef(null);
    const searchRef = React.useRef(null);
    const listRef = React.useRef(null);

    const getViolationTypeData = (type) => {
        return violationTypes.find(vt => 
            vt.type?.trim().toLowerCase() === type?.trim().toLowerCase()
        ) || null;
    };
    const selectedViolationType = getViolationTypeData(selectedViolation?.violation_type);

    const getViolationTypeColor = () => {
        return selectedViolationType
            ? { color: selectedViolationType?.label?.text_color, backgroundColor: selectedViolationType?.label?.background_color }
            : { color: '#f3f4f6', backgroundColor: '#1f2937' };
    };

    const scrollToItem = (index) => {
        if (!listRef.current) return;
        
        const element = listRef.current.children[index];
        if (!element) return;

        const container = listRef.current;
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const elementTop = element.offsetTop;
        const elementBottom = elementTop + element.clientHeight;

        if (elementTop < containerTop) {
            container.scrollTop = elementTop;
        } else if (elementBottom > containerBottom) {
            container.scrollTop = elementBottom - container.clientHeight;
        }
    };

    const handlePrint = () => {
        setIsBreakdownOpen(true);
        setTimeout(() => {
            window.print();
        }, 100);
    };

    const filteredViolations = React.useMemo(() => {
        if (!searchQuery) {
            return (() => {
                const mostCommon = violations.filter(v => v.most_common_violation === true);
                const others = violations.filter(v => v.most_common_violation !== true);
                if (mostCommon.length >= 10) {
                    return mostCommon.slice(0, 10);
                }
                return mostCommon.concat(others.slice(0, 10 - mostCommon.length));
            })();
        }
        return violations.filter(violation => {
            const searchLower = searchQuery.toLowerCase();
            return (
                violation.name.toLowerCase().includes(searchLower) ||
                violation.codes.some(code => code.toLowerCase().includes(searchLower))
            );
        });
    }, [searchQuery, violations]); 

    const handleKeyDown = (e) => {
        if (!isOpen) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => {
                    const nextIndex = prev < filteredViolations.length - 1 ? prev + 1 : prev;
                    scrollToItem(nextIndex);
                    return nextIndex;
                });
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => {
                    const nextIndex = prev > 0 ? prev - 1 : prev;
                    scrollToItem(nextIndex);
                    return nextIndex;
                });
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < filteredViolations.length) {
                    const selected = filteredViolations[selectedIndex];
                    console.log(selected);
                    setSelectedViolation(selected);
                    setSearchQuery(selected.name);
                    setIsOpen(false);
                    setIsBreakdownOpen(false);
                    setSelectedIndex(-1);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSelectedIndex(-1);
                break;
        }
    };

    React.useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @media print {
                @page {
                    size: A4 portrait;
                    margin: 10mm;
                }
                body * {
                    visibility: hidden;
                }
                html {
                    height: auto;
                    min-height: auto;
                    margin: 0 !important;
                    padding: 0 !important;
                }
                body {
                    height: auto;
                    min-height: auto;
                    margin: 0 !important;
                    padding: 0 !important;
                }
                #printable-area, #printable-area * {
                    visibility: visible;
                }
                #printable-area {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                    margin-top: 0 !important;
                }
                .card-container {
                    margin-top: 0 !important;
                }
                .violation-name {
                    font-size: 1.25rem !important;
                    line-height: 1.75rem;
                }
                .no-print {
                    display: none !important;
                }
                .print-force-show {
                    display: block !important;
                    visibility: visible !important;
                }
                #traffic-calculator-root {
                    margin-top: 0 !important;
                    margin-bottom: 0 !important;
                    min-height: 100% !important;
                }
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSelectedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
            
    React.useEffect(() => {
        setSelectedIndex(-1);
    }, [filteredViolations]);

    const convertToNumeric = (number) => {
        return Number(number);
    };
    
    const formatNumber = (num) => {
        const numericValue = Number(num);
        if (isNaN(numericValue)) return 0;
        
        return numericValue % 1 === 0 
            ? numericValue.toLocaleString()
            : numericValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    return (
        <div className="w-full max-w-[50rem] mx-auto p-4 font-sans">
            {postContent && (
                <div className="text-center mb-8 no-print" dangerouslySetInnerHTML={{ __html: postContent }} />
            )}

       {textSection && textSection.top_text && (
    <div className="m-5 no-print wp-content" dangerouslySetInnerHTML={{ __html: textSection.top_text }} />
)}

<div className="no-print violation-search-container" ref={dropdownRef}>
    <div className="search-icon-badge">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
        </svg>
    </div>
    
    <label className="violation-label" htmlFor="violation-code">Enter Your Violation Code</label>
    
    <div className="input-wrapper">
        <input 
            ref={searchRef}
            type="text" 
            id="violation-code"
            className="violation-input" 
            placeholder="e.g., VC 22349 or 25 mph over"
            value={searchQuery}
            onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsOpen(true);
            }}
            onClick={() => setIsOpen(true)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
        />
        {selectedViolation ? (
            <button 
                className="search-btn"
                onClick={() => {
                    setSelectedViolation(null);
                    setSearchQuery('');
                    setIsOpen(false);
                    setSelectedIndex(-1);
                }}
                aria-label="Clear"
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
            </button>
        ) : (
            <button className="search-btn" aria-label="Search">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
            </button>
        )}
    </div>
    
    <p className="helper-text">Quick search for California speeding violations</p>
    
    {isOpen && (
        <div
            ref={listRef} 
            className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto no-print"
        >
            {filteredViolations.map((violation, index) => (
                <button
                    key={violation.id}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 ${
                        index === selectedIndex ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => {
                        setSelectedViolation(violation);
                        setSearchQuery(violation.name);
                        setIsOpen(false);
                        setIsBreakdownOpen(false);
                        setSelectedIndex(-1);
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                >
                    <div className="font-medium">{violation.name}</div>
                    <div className="text-sm text-gray-500 mt-1">{violation.codes.join(", ")}</div>
                </button>
            ))}
            {filteredViolations.length === 0 && (
                <div className="px-4 py-3 text-gray-500">No violations found</div>
            )}
        </div>
    )}
</div>
<div className="text-sm text-gray-600 mt-4 mb-6 px-4 max-w-2xl mx-auto">
    <strong>
        Disclaimer:
    </strong>
    This is not legal advice. While we have tried our best to collect accurate data, this calculator only provides estimates. Actual fines, fees, consequences, and insurance impacts may vary. Verify your traffic school eligibility with the court.
</div>
            {selectedViolation && (
                <div className="space-y-4" id="printable-area">
                    <Card className="mt-6 p-6 card-container">
                        <div className="mb-4">
                            <div className="flex flex-col gap-2 justify-between items-start mb-2 sm:flex-row">
                                <h2 className="text-xl sm:text-sm font-semibold violation-name order-2 sm:order-1">{selectedViolation.name}</h2>
                                <span className={`whitespace-nowrap px-3 py-1 rounded-full text-sm font-medium order-1 sm:order-2`} style={getViolationTypeColor()}>
                                    {selectedViolation.violation_type}
                                </span>
                            </div>
                            <p className="text-gray-500 text-sm">{selectedViolation.codes.join(", ")}</p>
                        </div>

                        <p className="text-gray-600 mb-6">{selectedViolation.description}</p>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <div className="flex flex-col items-center text-center justify-center sm:flex-row sm:justify-between sm:text-left">
                                    <div>
                                        <p className="font-medium leading-[24px] mb-0">Base Fine</p>
                                        <Tooltip content="Initial fine amount before additional fees and penalties">
                                            <span className="text-sm text-gray-500 cursor-help">What's this?</span>
                                        </Tooltip>
                                    </div>
                                    <p className="text-xl font-semibold">${formatNumber(selectedViolation.fine)}</p>
                                </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <div className="flex flex-col items-center text-center justify-center sm:flex-row sm:justify-between sm:text-left">
                                    <div>
                                        <p className="font-medium leading-[24px] mb-0">Points</p>
                                        <Tooltip content="Points added to your driving record. Multiple points can lead to license suspension.">
                                            <span className="text-sm text-gray-500 cursor-help">What's this?</span>
                                        </Tooltip>
                                    </div>
                                    <p className="text-xl font-semibold">{formatNumber(selectedViolation.points)}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-red-50 border border-red-200 rounded-lg mb-6">
                            <button 
                                className="w-full p-4 flex justify-between items-center"
                                onClick={() => setIsBreakdownOpen(!isBreakdownOpen)}
                            >
                                <h3 className="text-red-800 font-semibold">Total Cost Breakdown</h3>
                                <div className="flex items-center gap-4 no-print">
                                    {!isBreakdownOpen && (
                                        <span className="text-red-800 font-semibold">
                                            ${formatNumber(selectedViolation.total_cost)}
                                        </span>
                                    )}
                                    <span className="material-icons text-red-800">
                                        {isBreakdownOpen ? 'expand_less' : 'expand_more'}
                                    </span>
                                </div>
                            </button>
                            
                            {isBreakdownOpen && (
                                <div className="p-4 border-t border-red-200">
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-red-700">Base Fine</span>
                                            <span>${formatNumber(selectedViolation.fine)}</span>
                                        </div>
                                        {selectedViolation.cost_breakdown.map((breakdown) => (
                                            <div className="flex justify-between">
                                                <span className="text-red-700">{breakdown.cost_description}</span>
                                                <span>${formatNumber(breakdown.price)}</span>
                                            </div>
                                        ))}
                                        <div className="border-t border-red-200 pt-2 mt-2">
                                            <div className="flex justify-between font-semibold text-red-800">
                                                <span>Total Due</span>
                                                <span>${formatNumber(selectedViolation.total_cost)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {selectedViolation.traffic_school_eligible && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h3 className="text-green-800 font-semibold mb-2">This Violation Is Eligible for Traffic School</h3>
                            <div className="mb-6">
                                <p className="text-green-700 mb-2">3 Simple Steps:</p>
                                <ol className="text-green-700 list-decimal pl-5 space-y-2">
                                <li>Get your court's approval for traffic school</li>
                                <li>Complete our course at your own pace</li>
                                <li>Pay only {price} after you pass</li>
                                </ol>
                            </div>
                            <div className="bg-white rounded-lg p-4 mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2 leading-[24px]">Why Choose Traffic School?</h4>
                                <ul className="text-gray-600 space-y-2">
                                <li className="flex items-center">
                                    <span className="text-green-500 mr-2">✓</span>
                                    No points on your record
                                </li>
                                <li className="flex items-center">
                                    <span className="text-green-500 mr-2">✓</span>
                                    Keep your insurance rates from increasing
                                </li>
                                <li className="flex items-center">
                                    <span className="text-green-500 mr-2">✓</span>
                                    Don't pay until you pass
                                </li>
                                <li className="flex items-center">
                                    <span className="text-green-500 mr-2">✓</span>
                                    Complete online at your own pace
                                </li>
                                </ul>
                            </div>
                            {eligible && (
                                <button 
                                    onClick={() => window.open(`${eligible.button_url}`, '_blank')}
                                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-200 w-full text-center mb-2"
                                >
                                    {eligible.button_text}
                                </button>
                            )}
                            <p className="text-sm text-green-700 text-center">No payment required until you pass!</p>
                            </div>
                        )}

                        {!selectedViolation.traffic_school_eligible && selectedViolationType && (
                            <div 
                                className="rounded-lg p-4 mt-4 border" 
                                style={{ 
                                    backgroundColor: selectedViolationType.box.background_color, 
                                    borderColor: selectedViolationType.box.outline_color 
                                }}
                            >
                                <h3 
                                    className="font-semibold mb-2" 
                                    style={{ color: selectedViolationType.box.title_color }}
                                >
                                    {selectedViolationType.title}
                                </h3>
                                <p 
                                    className="mb-4" 
                                    style={{ color: selectedViolationType.box.text_color }}
                                >
                                    {selectedViolationType.text}
                                </p>
                                {selectedViolationType.button?.link && (
                                    <button 
                                        onClick={() => window.open(selectedViolationType.button.link.url, '_blank')}
                                        className="px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-200 w-full text-center"
                                        style={{ 
                                            backgroundColor: selectedViolationType.button.button_color, 
                                            color: selectedViolationType.button.button_text_color 
                                        }}
                                    >
                                        {selectedViolationType.button.text}
                                    </button>
                                )}
                            </div>
                        )}
                    </Card>

                    {selectedViolation?.insurance?.affects_insurance && (
                        <Card>
                            <div className="p-6">
                                <h3 className="text-xl font-semibold text-blue-900 mb-4">
                                    {selectedViolation.insurance.title}
                                </h3>
                                
                                <div className="bg-blue-50 rounded-lg p-4">
                                    <h4 className="font-medium text-blue-900 mb-3 leading-[24px]">
                                        {selectedViolation.insurance.description}
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    {selectedViolation?.insurance?.cards?.map((card) => (
                                        <div className="bg-white p-4 rounded-lg text-center">
                                            <p className="text-gray-600 text-sm mb-1">{card.title}</p>
                                            <p className="text-xl font-semibold text-blue-600">${formatNumber(card.price)}</p>
                                            <p className="text-xs text-gray-500">{card.description}</p>
                                        </div>
                                    ))}
                                    </div>
                                </div>

                                {selectedViolation.traffic_school_eligible && (
                                    <div className={`bg-${selectedViolation.traffic_school_eligible ? 'green' : 'red'}-50 rounded-lg p-4 mt-6`}>
                                        <div className="flex items-start">
                                            <div className="text-green-500 mr-3">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                                    <line x1="12" y1="16" x2="12" y2="16"></line>
                                                </svg>
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-green-800 mb-1 leading-[24px]">
                                                    {selectedViolation?.insurance?.notification?.title}
                                                </h4>
                                                <p className="text-green-700 text-sm">
                                                    {selectedViolation?.insurance?.notification?.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card>
                    )}
                </div>
            )}
            
            {selectedViolation && (
                <div className="space-y-4 no-print">
                    <button
                        onClick={handlePrint}
                        className="w-full py-2 text-blue-600 hover:text-blue-700 border border-blue-200 rounded-lg mt-4"
                    >
                        Print Summary
                    </button>
                </div>
            )}

            {textSection && textSection.bottom_text && (
                <div className="m-5 no-print wp-content" dangerouslySetInnerHTML={{ __html: textSection.bottom_text }} />
            )}
        </div>
    );
}

ReactDOM.createRoot(document.getElementById('traffic-calculator-root')).render(
    <React.StrictMode>
        <TrafficCalculator />
    </React.StrictMode>
)
